/**
 * ワンバサダープロジェクト：寄付先団体の検索・選択
 * Custom Element として隔離。カートページのみで読み込むこと。
 */

(() => {
  const ATTR_ORG_NAME = '寄付先団体名';
  const ATTR_ORG_ID = '寄付先団体ID';
  const ATTR_DONATION_RATE = '寄付率';

  const LEGACY_ATTR_KEYS = [
    '寄付先',
    '希望寄付先団体名',
    '寄付先選択種別',
    'organization_id',
  ];

  const MIN_DONATION_PERCENTAGE = 0;
  const MAX_DONATION_PERCENTAGE = 10;

  const SAVE_DEBOUNCE_MS = 500;
  const OTHER_ORG_NAME_MAX_LENGTH = 100;
  const OMAKASE_LABEL = 'おまかせで寄付';
  const OTHER_LABEL = 'その他';
  const UNSELECTED_LABEL = '寄付先団体を選択してください';

  /**
   * @param {number|string|null|undefined} value
   * @returns {number}
   */
  function parseSortOrder(value) {
    if (value == null || value === '') return Number.MAX_SAFE_INTEGER;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  }

  /**
   * おまかせを最上、その他を最下、通常団体は sort_order 昇順（未記入は通常団体の末尾）
   * @param {Array<{name: string, sortOrder?: number|string|null}>} organizations
   * @returns {typeof organizations}
   */
  function sortOrganizations(organizations) {
    const omakase = [];
    const other = [];
    const regular = [];

    organizations.forEach((org) => {
      if (org.name === OMAKASE_LABEL) {
        omakase.push(org);
      } else if (org.name === OTHER_LABEL) {
        other.push(org);
      } else {
        regular.push(org);
      }
    });

    regular.sort((a, b) => parseSortOrder(a.sortOrder) - parseSortOrder(b.sortOrder));

    return [...omakase, ...regular, ...other];
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  function toHiragana(str) {
    return str.replace(/[\u30a1-\u30f6]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0x60)
    );
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function normalizeSearchText(text) {
    if (text == null) return '';

    let normalized = String(text);
    if (typeof normalized.normalize === 'function') {
      normalized = normalized.normalize('NFKC');
    }

    normalized = toHiragana(normalized).toLowerCase();
    normalized = normalized.replace(/[・\-ー_\u3000\s]+/g, '');
    return normalized.trim();
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  function normalizeOtherOrganizationName(value) {
    if (value == null) return '';

    let normalized = String(value).replace(/[\r\n]+/g, ' ');
    normalized = normalized.replace(/\s+/g, ' ').trim();

    if (normalized.length > OTHER_ORG_NAME_MAX_LENGTH) {
      normalized = normalized.slice(0, OTHER_ORG_NAME_MAX_LENGTH);
    }

    return normalized;
  }

  /**
   * @param {string} query
   * @param {string} searchText
   * @returns {boolean}
   */
  function matchesSearch(query, searchText) {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return true;

    const tokens = String(query)
      .trim()
      .split(/[\s\u3000]+/)
      .map((token) => normalizeSearchText(token))
      .filter(Boolean);

    if (tokens.length === 0) return true;

    const normalizedTarget = normalizeSearchText(searchText);
    return tokens.every((token) => normalizedTarget.includes(token));
  }

  /**
   * @returns {string}
   */
  function getCartUpdateUrl() {
    if (typeof routes !== 'undefined' && routes.cart_update_url) {
      return routes.cart_update_url;
    }
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/update.js`;
  }

  /**
   * @returns {RequestInit}
   */
  function getFetchConfig() {
    if (typeof fetchConfig === 'function') {
      return fetchConfig();
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  /**
   * Shopify money format 互換（minor units → 表示文字列）
   * @param {number} cents
   * @param {string} format
   * @returns {string}
   */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{amount}}';

    /**
     * @param {number} amount
     * @param {number} precision
     * @param {string} thousands
     * @param {string} decimal
     * @returns {string}
     */
    function formatWithDelimiters(amount, precision, thousands, decimal) {
      const number = (amount / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const value = (() => {
      switch (formatString.match(placeholderRegex)?.[1]) {
        case 'amount':
          return formatWithDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.');
        default:
          return formatWithDelimiters(cents, 2, ',', '.');
      }
    })();

    return formatString.replace(placeholderRegex, value);
  }

  /**
   * @param {number} totalCents
   * @param {number} percentage
   * @returns {number}
   */
  function calculateDonationCents(totalCents, percentage) {
    const safeTotal = Math.max(0, Math.trunc(totalCents || 0));
    const safePercentage = Number(percentage);
    if (!Number.isFinite(safePercentage) || safePercentage <= 0) return 0;

    // minor unit のまま整数演算し、端数は切り捨て
    return Math.floor((safeTotal * Math.round(safePercentage * 1000)) / (100 * 1000));
  }

  /**
   * @param {string|number} value
   * @returns {number}
   */
  function parsePercentage(value) {
    const parsed = Number(String(value ?? '').replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(parsed)) return MIN_DONATION_PERCENTAGE;
    return Math.min(
      MAX_DONATION_PERCENTAGE,
      Math.max(MIN_DONATION_PERCENTAGE, parsed)
    );
  }

  /**
   * Cart Attribute「寄付率」用。整数は "2"、小数は "2.5" のように末尾ゼロを付けない。
   * @param {string|number} value
   * @returns {string}
   */
  function formatDonationPercentage(value) {
    const parsed = parsePercentage(value);
    const tenths = Math.round(parsed * 10);
    const whole = Math.trunc(tenths / 10);
    const fraction = Math.abs(tenths % 10);
    if (fraction === 0) return String(whole);
    return `${whole}.${fraction}`;
  }

  class DonationOrganizationSelector extends HTMLElement {
    constructor() {
      super();

      /** @type {{ mode: 'omakase'|'registered'|'other'|null, organizationId: string, organizationUid: string, organizationName: string, otherOrganizationName: string }} */
      this.state = {
        mode: null,
        organizationId: '',
        organizationUid: '',
        organizationName: '',
        otherOrganizationName: '',
      };

      /** @type {Array<{id: string, uid?: string, name: string, searchText: string, sortOrder: number}>} */
      this.organizations = [];
      /** @type {Array<{id: string, uid?: string, name: string, searchText: string, sortOrder: number}>} */
      this.filteredOrganizations = [];

      this.donationPercentage = 2;
      this.cartTotalCents = 0;
      this.moneyFormat = '{{amount}}';
      this.locale = 'ja';
      this.currencyCode = 'JPY';
      this.clearLegacyAttributes = false;

      this.isOpen = false;
      this.highlightedIndex = -1;

      this.isSaving = false;
      this.hasSaveError = false;
      this.saveRequestId = 0;
      /** @type {{ organizationName: string, organizationId: string, donationPercentage: string }|null} */
      this.lastSavedAttributes = null;
      /** @type {Promise<boolean>|null} */
      this.pendingSavePromise = null;
      this.pendingResave = false;
      this.customInputDebounceTimer = null;
      this.checkoutOriginallyDisabled = false;
      this.isProcessingCheckout = false;
      this.bypassCheckoutGuard = false;
      this.cartUpdateUnsubscriber = undefined;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleSearchInput = this.handleSearchInput.bind(this);
      this.handleSearchKeydown = this.handleSearchKeydown.bind(this);
      this.handleCustomInput = this.handleCustomInput.bind(this);
      this.handleCustomBlur = this.handleCustomBlur.bind(this);
      this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.cacheElements();
      this.loadConfig();
      this.loadOrganizations();

      if (this.organizations.length === 0) {
        this.hidden = true;
        return;
      }

      this.bindEvents();
      this.bindCartEvents();
      this.filteredOrganizations = [...this.organizations];
      this.hideCustomField();
      this.renderOptions();
      this.updateDonationAmountDisplay();
      void this.restoreOrInitializeSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('click', this.handleCheckoutClick, true);

      this.trigger?.removeEventListener('click', this.handleTriggerClick);
      this.searchInput?.removeEventListener('input', this.handleSearchInput);
      this.searchInput?.removeEventListener('keydown', this.handleSearchKeydown);
      this.customInput?.removeEventListener('input', this.handleCustomInput);
      this.customInput?.removeEventListener('blur', this.handleCustomBlur);

      if (this.customInputDebounceTimer) clearTimeout(this.customInputDebounceTimer);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    cacheElements() {
      this.trigger = this.querySelector('[data-trigger]');
      this.triggerLabel = this.querySelector('[data-trigger-label]');
      this.panel = this.querySelector('[data-panel]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.listbox = this.querySelector('[data-listbox]');
      this.noResults = this.querySelector('[data-no-results]');
      this.customField = this.querySelector('[data-custom-field]');
      this.customInput = this.querySelector('[data-custom-input]');
      this.errors = this.querySelector('[data-errors]');
      this.saveStatus = this.querySelector('[data-save-status]');
      this.amountValue = this.querySelector('[data-donation-amount-value]');
      this.iconCheckUrl = this.dataset.iconCheck || '';
      this.cartForm = document.getElementById('cart');
      this.cacheCheckoutButton();
    }

    cacheCheckoutButton() {
      const checkoutButton = document.getElementById('checkout');
      if (checkoutButton && checkoutButton !== this.checkoutButton) {
        this.checkoutOriginallyDisabled = checkoutButton.disabled;
      }
      this.checkoutButton = checkoutButton;
    }

    getCheckoutButton() {
      this.cacheCheckoutButton();
      return this.checkoutButton;
    }

    loadConfig() {
      const configElement = this.querySelector('[data-donation-config]');
      let config = {};

      if (configElement) {
        try {
          config = JSON.parse(configElement.textContent || '{}');
        } catch (error) {
          console.error('[inumarche-donation] failed to parse config JSON', error);
        }
      }

      this.donationPercentage = parsePercentage(
        config.donationPercentage ?? this.dataset.donationPercentage ?? 2
      );
      this.cartTotalCents = Number(config.cartTotalCents ?? this.dataset.cartTotalCents ?? 0) || 0;
      this.moneyFormat = config.moneyFormat || this.dataset.moneyFormat || '{{amount}}';
      this.locale = config.locale || this.dataset.locale || 'ja';
      this.currencyCode = config.currencyCode || this.dataset.currencyCode || 'JPY';
      this.savedCartAttributes = config.savedAttributes || null;
      this.clearLegacyAttributes = Boolean(config.clearLegacyAttributes);
    }

    loadOrganizations() {
      const dataElement = this.querySelector('[data-donation-organizations]');
      if (!dataElement) return;

      try {
        const parsed = JSON.parse(dataElement.textContent || '[]');
        this.organizations = Array.isArray(parsed) ? sortOrganizations(parsed.slice()) : [];
      } catch (error) {
        console.error('[inumarche-donation] failed to parse organizations JSON', error);
        this.organizations = [];
      }
    }

    bindEvents() {
      this.trigger?.addEventListener('click', this.handleTriggerClick);
      this.searchInput?.addEventListener('input', this.handleSearchInput);
      this.searchInput?.addEventListener('keydown', this.handleSearchKeydown);
      this.customInput?.addEventListener('input', this.handleCustomInput);
      this.customInput?.addEventListener('blur', this.handleCustomBlur);
      document.addEventListener('click', this.handleDocumentClick);
    }

    bindCartEvents() {
      document.addEventListener('click', this.handleCheckoutClick, true);
      this.cacheCheckoutButton();

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event?.cartData || typeof event.cartData.total_price !== 'number') return;
        this.cartTotalCents = event.cartData.total_price;
        this.updateDonationAmountDisplay();
      });
    }

    updateDonationAmountDisplay() {
      if (!this.amountValue) return;
      const donationCents = calculateDonationCents(this.cartTotalCents, this.donationPercentage);
      this.amountValue.textContent = formatMoney(donationCents, this.moneyFormat);
    }

    findOrganization(savedId, savedName) {
      const id = String(savedId || '');
      if (id) {
        const byFieldId = this.organizations.find((org) => org.id && String(org.id) === id);
        if (byFieldId) return byFieldId;

        const byUid = this.organizations.find((org) => org.uid && String(org.uid) === id);
        if (byUid) return byUid;
      }

      if (savedName) {
        return this.organizations.find((org) => org.name === savedName) || null;
      }

      return null;
    }

    /**
     * @param {{ name: string }} organization
     * @returns {'omakase'|'other'|'registered'}
     */
    getOrganizationType(organization) {
      if (organization.name === OMAKASE_LABEL) return 'omakase';
      if (organization.name === OTHER_LABEL) return 'other';
      return 'registered';
    }

    getOmakaseEntry() {
      return this.organizations.find((org) => org.name === OMAKASE_LABEL) || null;
    }

    donationRateNeedsResave() {
      const savedRate = String(this.savedCartAttributes?.donationPercentage ?? '').trim();
      return savedRate !== this.getDonationPercentageAttributeValue();
    }

    /**
     * @returns {string}
     */
    getDonationPercentageAttributeValue() {
      return formatDonationPercentage(
        this.dataset.donationPercentage || this.donationPercentage
      );
    }

    async restoreOrInitializeSelection() {
      const savedName = normalizeOtherOrganizationName(
        this.savedCartAttributes?.organizationName || ''
      );
      const savedId = String(this.savedCartAttributes?.organizationId || '');
      const donationRateNeedsResave = this.donationRateNeedsResave();

      if (savedId) {
        const matched = this.findOrganization(savedId, '');
        if (matched) {
          const type = this.getOrganizationType(matched);
          const needsResave =
            savedId !== String(matched.id || '') ||
            this.clearLegacyAttributes ||
            donationRateNeedsResave;

          if (type === 'omakase') {
            this.applyOmakaseSelection({ persist: needsResave });
          } else if (type === 'other') {
            this.applyOtherSelection('', { persist: needsResave });
          } else {
            this.applyRegisteredSelection(matched, { persist: needsResave });
          }

          if (!needsResave) {
            this.lastSavedAttributes = this.buildCurrentAttributes();
          }
          return;
        }
      }

      if (savedName === OMAKASE_LABEL) {
        const needsResave = !savedId || this.clearLegacyAttributes || donationRateNeedsResave;
        this.applyOmakaseSelection({ persist: needsResave });
        if (!needsResave) {
          this.lastSavedAttributes = this.buildCurrentAttributes();
        }
        return;
      }

      if (!savedId && savedName) {
        const needsResave = this.clearLegacyAttributes || donationRateNeedsResave;
        this.applyOtherSelection(savedName, { persist: needsResave });
        if (!needsResave) {
          this.lastSavedAttributes = this.buildCurrentAttributes();
        }
        return;
      }

      if (!savedName && !savedId) {
        const omakaseEntry = this.getOmakaseEntry();
        if (omakaseEntry) {
          this.applyOmakaseSelection({ persist: true });
        } else {
          console.warn(
            '[inumarche-donation] 「おまかせで寄付」がメタオブジェクトに登録されていません。'
          );
          this.applyUnselectedState();
        }
        return;
      }

      console.warn('[inumarche-donation] 保存された寄付先を復元できませんでした。');
      this.applyUnselectedState();
    }

    handleTriggerClick(event) {
      event.preventDefault();
      if (this.isOpen) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    }

    handleDocumentClick(event) {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      this.closePanel();
    }

    handleSearchInput() {
      const query = this.searchInput?.value ?? '';
      this.filterOrganizations(query);
      this.highlightedIndex = -1;
      this.renderOptions();
      this.openPanel();
    }

    handleSearchKeydown(event) {
      const options = this.getOptionButtons();

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) this.openPanel();
          this.highlightedIndex = Math.min(this.highlightedIndex + 1, options.length - 1);
          this.updateHighlight(options);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!this.isOpen) this.openPanel();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          this.updateHighlight(options);
          break;
        case 'Enter':
          if (!this.isOpen || this.highlightedIndex < 0) return;
          event.preventDefault();
          options[this.highlightedIndex]?.click();
          break;
        case 'Escape':
          event.preventDefault();
          this.closePanel();
          this.trigger?.focus();
          break;
        default:
          break;
      }
    }

    handleCustomInput() {
      this.state.otherOrganizationName = this.customInput?.value ?? '';
      this.clearErrors();
      this.scheduleDebouncedSave();
    }

    handleCustomBlur() {
      this.flushCustomInputDebounce();
      this.normalizeAndApplyCustomInput();
      if (this.state.mode === 'other') {
        this.scheduleSave();
      }
    }

    filterOrganizations(query) {
      this.filteredOrganizations = this.organizations.filter((org) =>
        matchesSearch(query, org.searchText)
      );
    }

    getOptionButtons() {
      return Array.from(this.listbox?.querySelectorAll('[data-option-button]') ?? []);
    }

    updateHighlight(options) {
      options.forEach((button, index) => {
        const isHighlighted = index === this.highlightedIndex;
        button.classList.toggle('inumarche-donation__option--highlighted', isHighlighted);
        if (isHighlighted) button.scrollIntoView({ block: 'nearest' });
      });
    }

    openPanel() {
      if (!this.panel) return;
      this.isOpen = true;
      this.panel.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.searchInput?.setAttribute('aria-expanded', 'true');
      this.renderOptions();
      requestAnimationFrame(() => this.searchInput?.focus());
    }

    closePanel() {
      if (!this.panel) return;
      this.isOpen = false;
      this.panel.hidden = true;
      this.highlightedIndex = -1;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      if (this.searchInput) this.searchInput.value = '';
      this.filteredOrganizations = [...this.organizations];
    }

    renderOptions() {
      if (!this.listbox) return;

      const query = this.searchInput?.value ?? '';
      const hasQuery = query.trim().length > 0;
      const showNoResults = hasQuery && this.filteredOrganizations.length === 0;

      if (this.noResults) this.noResults.hidden = !showNoResults;

      this.listbox.innerHTML = '';

      this.filteredOrganizations.forEach((org) => {
        this.listbox.appendChild(
          this.createOptionButton({
            type: this.getOrganizationType(org),
            id: org.id || '',
            uid: org.uid || '',
            name: org.name,
          })
        );
      });

      this.updateOptionStates();
    }

    /**
     * @param {{ type: 'omakase'|'registered'|'other', id: string, uid?: string, name: string }} option
     * @returns {HTMLButtonElement}
     */
    createOptionButton(option) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'inumarche-donation__option';
      if (option.type === 'other') {
        button.classList.add('inumarche-donation__option--other');
      }
      button.setAttribute('role', 'option');
      button.setAttribute('data-option-button', '');
      button.setAttribute('data-option-type', option.type);
      button.setAttribute('data-organization-id', option.id || '');
      button.setAttribute('data-organization-uid', option.uid || '');
      button.setAttribute('data-organization-name', option.name);

      const iconWrap = document.createElement('span');
      iconWrap.className = 'inumarche-donation__option-icon';
      iconWrap.setAttribute('aria-hidden', 'true');

      const checkImg = document.createElement('img');
      checkImg.className = 'inumarche-donation__option-icon-check';
      checkImg.src = this.iconCheckUrl;
      checkImg.alt = '';
      checkImg.width = 24;
      checkImg.height = 24;
      checkImg.loading = 'lazy';

      iconWrap.appendChild(checkImg);

      const label = document.createElement('span');
      label.className = 'inumarche-donation__option-label';
      label.textContent = option.name;

      button.appendChild(iconWrap);
      button.appendChild(label);

      button.addEventListener('click', () => {
        if (option.type === 'omakase') {
          this.selectOmakase();
        } else if (option.type === 'other') {
          this.selectOther();
        } else {
          this.selectRegistered({
            id: option.id || '',
            uid: option.uid || '',
            name: option.name,
          });
        }
      });

      return button;
    }

    updateOptionStates() {
      const options = this.getOptionButtons();
      options.forEach((button) => {
        const type = button.getAttribute('data-option-type');
        let isSelected = false;

        if (type === 'omakase') {
          isSelected = this.state.mode === 'omakase';
        } else if (type === 'other') {
          isSelected = this.state.mode === 'other';
        } else {
          const optionUid = button.getAttribute('data-organization-uid') || '';
          isSelected =
            this.state.mode === 'registered' &&
            optionUid !== '' &&
            String(this.state.organizationUid) === String(optionUid);
        }

        button.classList.toggle('inumarche-donation__option--selected', isSelected);
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }

    updateTriggerLabel(label) {
      if (this.triggerLabel) this.triggerLabel.textContent = label;
    }

    hideCustomField() {
      if (!this.customField) return;
      this.customField.hidden = true;
    }

    showCustomField() {
      if (!this.customField) return;
      this.customField.hidden = false;
    }

    selectOmakase() {
      this.applyOmakaseSelection({ persist: true });
      this.clearErrors();
      this.closePanel();
    }

    selectRegistered(organization) {
      this.applyRegisteredSelection(organization, { persist: true });
      this.clearErrors();
      this.closePanel();
    }

    selectOther() {
      this.applyOtherSelection('', { persist: false });
      this.clearErrors();
      this.closePanel();
      this.customInput?.focus();
    }

    /**
     * @param {{ persist?: boolean }} [options]
     */
    applyOmakaseSelection(options = {}) {
      const { persist = true } = options;
      const omakaseEntry = this.getOmakaseEntry();

      if (!omakaseEntry) {
        console.warn(
          '[inumarche-donation] 「おまかせで寄付」がメタオブジェクトに登録されていません。'
        );
        this.applyUnselectedState();
        return;
      }

      this.state.mode = 'omakase';
      this.state.organizationId = String(omakaseEntry.id || '');
      this.state.organizationUid = String(omakaseEntry.uid || '');
      this.state.organizationName = OMAKASE_LABEL;
      this.state.otherOrganizationName = '';
      if (this.customInput) this.customInput.value = '';
      this.hideCustomField();
      this.updateTriggerLabel(OMAKASE_LABEL);
      this.updateOptionStates();
      if (persist) this.scheduleSave();
    }

    applyUnselectedState() {
      this.state.mode = null;
      this.state.organizationId = '';
      this.state.organizationUid = '';
      this.state.organizationName = '';
      this.state.otherOrganizationName = '';
      if (this.customInput) this.customInput.value = '';
      this.hideCustomField();
      this.updateTriggerLabel(UNSELECTED_LABEL);
      this.updateOptionStates();
    }

    /**
     * @param {{ id: string, uid?: string, name: string }} organization
     * @param {{ persist?: boolean }} [options]
     */
    applyRegisteredSelection(organization, options = {}) {
      const { persist = true } = options;
      this.state.mode = 'registered';
      this.state.organizationId = String(organization.id || '');
      this.state.organizationUid = String(organization.uid || '');
      this.state.organizationName = organization.name;
      this.state.otherOrganizationName = '';
      if (this.customInput) this.customInput.value = '';
      this.hideCustomField();
      this.updateTriggerLabel(organization.name);
      this.updateOptionStates();
      if (persist) this.scheduleSave();
    }

    /**
     * @param {string} otherName
     * @param {{ persist?: boolean }} [options]
     */
    applyOtherSelection(otherName, options = {}) {
      const { persist = true } = options;
      this.state.mode = 'other';
      this.state.organizationId = '';
      this.state.organizationUid = '';
      this.state.organizationName = '';
      this.state.otherOrganizationName = otherName;
      if (this.customInput) this.customInput.value = otherName;
      this.showCustomField();
      this.updateTriggerLabel(OTHER_LABEL);
      this.updateOptionStates();
      if (persist) this.scheduleSave();
    }

    normalizeAndApplyCustomInput() {
      if (!this.customInput) return;
      const normalized = normalizeOtherOrganizationName(this.customInput.value);
      if (this.customInput.value !== normalized) {
        this.customInput.value = normalized;
      }
      this.state.otherOrganizationName = normalized;
    }

    flushCustomInputDebounce() {
      if (!this.customInputDebounceTimer) return;
      clearTimeout(this.customInputDebounceTimer);
      this.customInputDebounceTimer = null;
    }

    scheduleDebouncedSave() {
      this.flushCustomInputDebounce();
      this.customInputDebounceTimer = setTimeout(() => {
        this.customInputDebounceTimer = null;
        this.normalizeAndApplyCustomInput();
        this.scheduleSave();
      }, SAVE_DEBOUNCE_MS);
    }

    /**
     * @returns {{ organizationName: string, organizationId: string, donationPercentage: string }|null}
     */
    buildCurrentAttributes() {
      if (!this.state.mode) return null;

      const donationPercentage = this.getDonationPercentageAttributeValue();

      if (this.state.mode === 'other') {
        const name = normalizeOtherOrganizationName(this.state.otherOrganizationName);
        if (!name) return null;
        return {
          organizationName: name,
          organizationId: '',
          donationPercentage,
        };
      }

      if (this.state.mode === 'omakase') {
        return {
          organizationName: OMAKASE_LABEL,
          organizationId: String(this.state.organizationId || ''),
          donationPercentage,
        };
      }

      return {
        organizationName: this.state.organizationName,
        organizationId: String(this.state.organizationId || ''),
        donationPercentage,
      };
    }

    /**
     * @param {{ organizationName: string, organizationId: string, donationPercentage: string }|null} attrs
     * @returns {boolean}
     */
    isSameAsLastSaved(attrs) {
      if (!attrs || !this.lastSavedAttributes) return false;
      return (
        attrs.organizationName === this.lastSavedAttributes.organizationName &&
        attrs.organizationId === this.lastSavedAttributes.organizationId &&
        attrs.donationPercentage === this.lastSavedAttributes.donationPercentage
      );
    }

    scheduleSave() {
      const attrs = this.buildCurrentAttributes();
      if (!attrs) return Promise.resolve(false);
      if (this.isSameAsLastSaved(attrs) && !this.hasSaveError) {
        return Promise.resolve(true);
      }

      if (this.isSaving) {
        this.pendingResave = true;
        return this.pendingSavePromise || Promise.resolve(false);
      }

      this.pendingSavePromise = this.performSave();
      return this.pendingSavePromise;
    }

    async ensureSaved() {
      this.flushCustomInputDebounce();
      this.normalizeAndApplyCustomInput();

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const attrs = this.buildCurrentAttributes();
        if (!attrs) return false;
        if (this.isSameAsLastSaved(attrs) && !this.hasSaveError) return true;

        await this.scheduleSave();
        if (this.pendingSavePromise) await this.pendingSavePromise;
        if (this.hasSaveError) return false;
      }

      return this.isSameAsLastSaved(this.buildCurrentAttributes()) && !this.hasSaveError;
    }

    async performSave() {
      const attrs = this.buildCurrentAttributes();
      if (!attrs) return false;

      if (this.isSameAsLastSaved(attrs) && !this.hasSaveError) return true;

      const requestId = ++this.saveRequestId;
      this.isSaving = true;
      this.hasSaveError = false;
      this.updateSaveStatus('saving');

      const payload = {
        [ATTR_ORG_NAME]: attrs.organizationName,
        [ATTR_ORG_ID]: attrs.organizationId,
        [ATTR_DONATION_RATE]: attrs.donationPercentage,
      };

      LEGACY_ATTR_KEYS.forEach((key) => {
        payload[key] = '';
      });

      try {
        const body = JSON.stringify({ attributes: payload });
        const response = await fetch(getCartUpdateUrl(), { ...getFetchConfig(), body });
        const cart = await response.json();

        if (!response.ok) {
          throw new Error(cart?.description || cart?.message || `Cart update failed (${response.status})`);
        }

        if (!cart || (cart.status && Number(cart.status) >= 400)) {
          throw new Error(cart?.description || cart?.message || 'Cart update failed');
        }

        if (requestId !== this.saveRequestId) {
          return false;
        }

        const latest = this.buildCurrentAttributes();
        if (
          latest &&
          latest.organizationName === attrs.organizationName &&
          latest.organizationId === attrs.organizationId &&
          latest.donationPercentage === attrs.donationPercentage
        ) {
          this.lastSavedAttributes = attrs;
          this.hasSaveError = false;
          this.clearErrors();
          this.updateSaveStatus('success');
        } else {
          this.pendingResave = true;
        }

        return true;
      } catch (error) {
        if (requestId === this.saveRequestId) {
          console.error('[inumarche-donation] failed to save cart attributes', error);
          this.hasSaveError = true;
          this.updateSaveStatus('error');
        }
        return false;
      } finally {
        if (requestId === this.saveRequestId) {
          this.isSaving = false;
          this.pendingSavePromise = null;

          if (this.pendingResave) {
            this.pendingResave = false;
            void this.scheduleSave();
          }
        }
      }
    }

    setCheckoutDisabled(disabled) {
      const checkoutButton = this.getCheckoutButton();
      if (!checkoutButton) return;

      if (disabled) {
        if (checkoutButton.dataset.donationSaveDisabled !== 'true') {
          this.checkoutOriginallyDisabled = checkoutButton.disabled;
          checkoutButton.dataset.donationSaveDisabled = 'true';
          checkoutButton.disabled = true;
        }
        return;
      }

      if (checkoutButton.dataset.donationSaveDisabled === 'true') {
        delete checkoutButton.dataset.donationSaveDisabled;
        checkoutButton.disabled = this.checkoutOriginallyDisabled;
      }
    }

    /**
     * @param {'saving'|'success'|'error'|''} status
     */
    updateSaveStatus(status) {
      if (!this.saveStatus) return;

      this.saveStatus.classList.remove(
        'inumarche-donation__status--saving',
        'inumarche-donation__status--success',
        'inumarche-donation__status--error'
      );

      if (!status || status === 'saving' || status === 'success') {
        this.saveStatus.hidden = true;
        this.saveStatus.textContent = '';
        return;
      }

      const messages = {
        error: '寄付先情報を保存できませんでした。時間をおいて再度お試しください。',
      };

      this.saveStatus.hidden = false;
      this.saveStatus.textContent = messages[status] || '';
      this.saveStatus.classList.add(`inumarche-donation__status--${status}`);
    }

    clearErrors() {
      if (!this.errors) return;
      this.errors.textContent = '';
      this.errors.hidden = true;
      this.classList.remove('inumarche-donation--has-error');
    }

    /**
     * @param {string} message
     */
    showError(message) {
      if (!this.errors || !message) return;
      this.errors.textContent = message;
      this.errors.hidden = false;
      this.classList.add('inumarche-donation--has-error');
    }

    focusError() {
      if (this.errors && !this.errors.hidden) {
        this.errors.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        this.errors.setAttribute('tabindex', '-1');
        this.errors.focus({ preventScroll: true });
        return;
      }

      if (this.state.mode === 'other' && this.customInput) {
        this.customInput.focus();
        return;
      }

      this.trigger?.focus();
    }

    /**
     * @returns {{ valid: boolean, message?: string }}
     */
    validateForCheckout() {
      if (!this.state.mode) {
        return { valid: false, message: '寄付先団体を選択してください。' };
      }

      if (this.state.mode === 'other') {
        const otherName = normalizeOtherOrganizationName(this.state.otherOrganizationName);
        if (!otherName) {
          return { valid: false, message: '希望する団体名を入力してください。' };
        }
      }

      if (this.state.mode === 'omakase' || this.state.mode === 'registered') {
        if (!this.state.organizationName) {
          return { valid: false, message: '寄付先団体を選択してください。' };
        }
      }

      if (this.hasSaveError) {
        return {
          valid: false,
          message: '寄付先情報を保存できませんでした。時間をおいて再度お試しください。',
        };
      }

      return { valid: true };
    }

    /**
     * @param {Element|null} element
     * @returns {boolean}
     */
    isCartPageCheckoutButton(element) {
      if (!(element instanceof HTMLButtonElement)) return false;
      if (element.name !== 'checkout') return false;
      return element.getAttribute('form') === 'cart' || element.form?.id === 'cart';
    }

    handleCheckoutClick(event) {
      if (this.bypassCheckoutGuard) return;

      const checkoutButton = event.target.closest('button[name="checkout"]');
      if (!checkoutButton || !this.isCartPageCheckoutButton(checkoutButton)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.processCheckoutRequest();
    }

    async processCheckoutRequest() {
      if (this.isProcessingCheckout) return;

      this.isProcessingCheckout = true;
      this.setCheckoutDisabled(true);

      try {
        this.clearErrors();

        const validationBeforeSave = this.validateForCheckout();
        if (!validationBeforeSave.valid) {
          this.showError(validationBeforeSave.message || '寄付先団体を選択してください。');
          this.focusError();
          this.isProcessingCheckout = false;
          this.setCheckoutDisabled(false);
          return;
        }

        const saved = await this.ensureSaved();
        if (!saved) {
          this.showError(
            '寄付先情報を保存できませんでした。時間をおいて再度お試しください。'
          );
          this.focusError();
          this.isProcessingCheckout = false;
          this.setCheckoutDisabled(false);
          return;
        }

        const validation = this.validateForCheckout();
        if (!validation.valid) {
          this.showError(validation.message || '寄付先団体を選択してください。');
          this.focusError();
          this.isProcessingCheckout = false;
          this.setCheckoutDisabled(false);
          return;
        }

        const checkoutButton = this.getCheckoutButton();
        const form = this.cartForm || document.getElementById('cart');

        this.setCheckoutDisabled(false);

        // 既存カートフォーム送信を優先（テーマのチェックアウト前処理を維持）
        if (form && typeof form.requestSubmit === 'function' && checkoutButton) {
          this.isProcessingCheckout = false;
          form.requestSubmit(checkoutButton);
          return;
        }

        if (checkoutButton) {
          this.bypassCheckoutGuard = true;
          this.isProcessingCheckout = false;
          checkoutButton.click();
          this.bypassCheckoutGuard = false;
          return;
        }

        if (form) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '1';
          form.appendChild(hidden);
          this.isProcessingCheckout = false;
          form.submit();
          return;
        }

        const root = window.Shopify?.routes?.root || '/';
        window.location.assign(`${root}checkout`);
      } catch (error) {
        console.error('[inumarche-donation] checkout failed', error);
        this.showError('購入手続きに進めませんでした。時間をおいて再度お試しください。');
        this.focusError();
        this.isProcessingCheckout = false;
        this.bypassCheckoutGuard = false;
        this.setCheckoutDisabled(false);
      }
    }
  }

  if (!customElements.get('donation-organization-selector')) {
    customElements.define('donation-organization-selector', DonationOrganizationSelector);
  }
})();
